import { useEffect, useRef, type CSSProperties } from 'react';

/**
 * Live Sky on Main cloud tile (RentCafe asset) — soft grey clouds with
 * palette transparency over light section washes. Same file used on
 * https://www.skyonmain.com/ via `.fixed-background`.
 */
export const GEOMETRIC_PATTERN_URL =
  'https://resource.rentcafe.com/image/upload/q_auto,f_auto/s3/2/5458/coulds%20sky%20png%20background-half%20opacity.png';

/** Native 500×500 tile. */
export const GEOMETRIC_PATTERN_SIZE = '500px 500px';

/** Fraction of scroll speed the pattern follows; lower = stronger parallax. */
const PARALLAX_FACTOR = 0.4;

const patternStyle: CSSProperties = {
  backgroundImage: `url(${GEOMETRIC_PATTERN_URL})`,
  backgroundRepeat: 'repeat',
  backgroundSize: GEOMETRIC_PATTERN_SIZE,
  backgroundPosition: 'left top',
  willChange: 'background-position',
};

/** Soft taupe wash used behind the cloud tile on the live homepage. */
const washStyle: CSSProperties = {
  backgroundColor: 'rgba(177, 172, 172, 0.14)',
};

/**
 * Tile origin is pinned to page coordinates (offset by scroll), so the cloud
 * pattern drifts slower than page content — matching the live fixed-background
 * parallax feel inside preview chrome where attachment:fixed is unreliable.
 */
export function GeometricPatternBackground() {
  const patternRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = patternRef.current;
    if (!el) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const factor = reduceMotion ? 0 : PARALLAX_FACTOR;
    let frame = 0;

    const update = () => {
      frame = 0;
      const scrollY = window.scrollY;
      const pageTop = el.getBoundingClientRect().top + scrollY;
      el.style.backgroundPosition = `left ${scrollY * factor - pageTop}px`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={washStyle}
      />
      <div
        ref={patternRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={patternStyle}
      />
    </>
  );
}
